// TanStack Query hooks for the customers feature.
// Backed by local BE /customers endpoints (NOT direct CENTER Appwrite calls).
//
// BE endpoints (handler.go + note/handler.go):
//   GET    /customers                 → list (limit/offset/tag)
//   GET    /customers/upcoming        → upcoming birthdays/anniversaries
//   GET    /customers/lookup?phone    → lookup by phone
//   GET    /customers/:id             → fetch one
//   POST   /customers                 → hộp "Thêm khách": số đã có hồ sơ → 409 customer_phone_exists
//   PUT    /customers/:id             → patch (partial update, incl. tags)
//   POST   /customers/:id/sync-center → sync to CENTER hub
//   GET    /customers/:id/notes       → list notes (cross-order CRM)
//   POST   /customers/:id/notes       → add customer-level note
//   PUT    /notes/:noteId             → update note
//   DELETE /notes/:noteId             → delete note

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import { serverErrorCode, serverErrorMessage } from "@/lib/api/error-message";
import type { CustomerFormValues, CustomerPatchValues } from "./schemas";
import type { components } from "@/lib/api/generated/schema";

type CustomerInput = components["schemas"]["CustomerInput"];
type CustomerPatch = components["schemas"]["CustomerPatch"];
type CustomerNoteInput = components["schemas"]["CustomerNoteInput"];
type NoteUpdateInput = components["schemas"]["NoteUpdateInput"];

export type Customer = components["schemas"]["Customer"];
export type CustomerCrmProfile = components["schemas"]["CustomerCrmProfile"];
export type CustomerLookupResponse = components["schemas"]["CustomerLookupResponse"];
export type CustomerPage = components["schemas"]["CustomerPage"];
export type Note = components["schemas"]["OrderNote"];

// ══════════ QUERY KEYS ══════════
export const customerKeys = {
  all: ["customers"] as const,
  list: (params?: { limit?: number; offset?: number; tag?: string }) =>
    ["customers", "list", params ?? {}] as const,
  page: (params: Record<string, string | number | undefined>) =>
    ["customers", "page", params] as const,
  detail: (id: string) => ["customers", "detail", id] as const,
  upcoming: (days: number) => ["customers", "upcoming", days] as const,
  notes: (id: string) => ["customers", "notes", id] as const,
  // Ba khoá dưới nằm dưới gốc của mảng chủ (complaints / reviews /
  // customer-chats) chứ không dưới "customers": ghi ở trang Khiếu nại, Đánh giá
  // hay Chat khách vô hiệu theo gốc của mảng đó là trang hồ sơ khách tự tươi.
  complaints: (id: string) => ["complaints", "customer", id] as const,
  reviews: (id: string) => ["reviews", "customer", id] as const,
  chat: (id: string) => ["customer-chats", "customer", id] as const,
};

// ══════════ LIST ══════════

export function useCustomers(
  limit = 50,
  offset = 0,
  tag?: string,
) {
  return useQuery({
    queryKey: customerKeys.list(tag ? { limit, offset, tag } : { limit, offset }),
    queryFn: async () => {
      const query = tag
        ? { limit, offset, tag }
        : { limit, offset };
      const { data, error } = await api.GET("/customers", { params: { query } });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được danh sách khách hàng"));
      return data;
    },
    staleTime: 30_000,
  });
}

// ══════════ PAGE (server-side search + total) ══════════

export interface CustomerPageQuery {
  q?: string;
  tag?: string;
  brand?: string;
  segment?: "new";
  limit: number;
  offset: number;
}

/**
 * useCustomersPage — server-paginated /customers/page. Pass q/tag/brand/limit/offset;
 * BE returns `{items, total}`. Replace useCustomers + client-filter pattern
 * to support 5k+ customers.
 */
export function useCustomersPage(qs: CustomerPageQuery, opts?: { enabled?: boolean }) {
  const params: Record<string, string | number> = { limit: qs.limit, offset: qs.offset };
  if (qs.q) params["q"] = qs.q;
  if (qs.tag) params["tag"] = qs.tag;
  if (qs.brand) params["brand"] = qs.brand;
  if (qs.segment) params["segment"] = qs.segment;
  return useQuery({
    queryKey: customerKeys.page(params),
    queryFn: async () => {
      const { data, error } = await api.GET("/customers/page", {
        params: { query: params },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được danh sách khách hàng"));
      return data;
    },
    staleTime: 30_000,
    enabled: opts?.enabled ?? true,
  });
}

// ══════════ DETAIL ══════════
// BE has GET /customers/:id since migration 0011's handler additions.

export function useCustomer(id: string | null) {
  return useQuery({
    queryKey: customerKeys.detail(id ?? ""),
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await api.GET("/customers/{id}", {
        params: { path: { id: id! } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tìm thấy khách hàng"));
      return data;
    },
    staleTime: 30_000,
  });
}

// ══════════ UPCOMING (birthday/anniversary banner) ══════════

export function useUpcomingCustomers(days = 14, opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: customerKeys.upcoming(days),
    queryFn: async () => {
      const { data, error } = await api.GET("/customers/upcoming", {
        params: { query: { days } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được danh sách sắp tới"));
      return data;
    },
    staleTime: 5 * 60_000, // 5 min — birthday list rarely changes
    enabled: opts?.enabled ?? true,
  });
}

// ══════════ LOOKUP BY PHONE ══════════

/**
 * Lookup a customer by phone. Returns the local row paired with the CENTER
 * CRM profile — either can be null. 404 maps to `{local: null, crmProfile: null}`
 * so callers can treat the absence uniformly without checking response.status.
 */
export function useLookupCustomer(phone: string | null) {
  return useQuery({
    queryKey: ["customers", "lookup", phone ?? ""],
    enabled: !!phone && phone.length >= 9,
    queryFn: async (): Promise<CustomerLookupResponse> => {
      const { data, response } = await api.GET("/customers/lookup", {
        params: { query: { phone: phone! } },
      });
      if (response.status === 404 || !data) {
        return { local: null, crmProfile: null };
      }
      return data;
    },
    staleTime: 60_000,
    retry: false,
  });
}

// ══════════ UPSERT ══════════

/**
 * Hộp "Thêm khách" gặp số đã có hồ sơ (máy chủ trả 409 customer_phone_exists
 * kèm tên hồ sơ cũ — kiểm thử đóng vai 17/10/2026, D03). Mang theo số đã gõ
 * để màn mở được hồ sơ cũ.
 */
export class CustomerPhoneExistsError extends Error {
  readonly phone: string;
  constructor(message: string, phone: string) {
    super(message);
    this.name = "CustomerPhoneExistsError";
    this.phone = phone;
  }
}

/** Mã hồ sơ khách theo số điện thoại; không có hồ sơ → null. */
export async function findCustomerIdByPhone(phone: string): Promise<string | null> {
  const { data } = await api.GET("/customers/lookup", { params: { query: { phone } } });
  return data?.local?.id ?? null;
}

export function useUpsertCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CustomerFormValues) => {
      // Dựng thân từng ô, không ép kiểu `as CustomerInput`: lần ép trước che mất
      // việc máy chủ không nhận sinh nhật, kỷ niệm, nhãn (D01). Ô trống không gửi
      // (exactOptionalPropertyTypes).
      const body: CustomerInput = { phone: input.phone, name: input.name };
      const optional = ["gender", "email", "branch", "source", "type", "company", "notes", "birthday", "anniversary"] as const;
      for (const key of optional) {
        const value = input[key];
        if (value) body[key] = value;
      }
      if (input.tags?.length) body.tags = input.tags;
      const { data, error } = await api.POST("/customers", { body });
      if (error || !data) {
        const message = serverErrorMessage(error, "Không lưu được khách hàng");
        if (serverErrorCode(error) === "customer_phone_exists") {
          throw new CustomerPhoneExistsError(message, input.phone);
        }
        throw new Error(message);
      }
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: customerKeys.all }),
  });
}

// ══════════ PATCH ══════════

export function usePatchCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: CustomerPatchValues }) => {
      const { data, error } = await api.PUT("/customers/{id}", {
        params: { path: { id } },
        body: input as CustomerPatch,
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không cập nhật được khách hàng"));
      return data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: customerKeys.all });
      qc.invalidateQueries({ queryKey: customerKeys.detail(vars.id) });
    },
  });
}

// ══════════ SYNC CENTER ══════════

export function useSyncCenter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await api.POST("/customers/{id}/sync-center", {
        params: { path: { id } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Đồng bộ CENTER thất bại"));
      return data;
    },
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: customerKeys.all });
      qc.invalidateQueries({ queryKey: customerKeys.detail(id) });
    },
  });
}

// ══════════ SYNC AVATAR ══════════

/**
 * useSyncAvatar — enqueue an avatar re-sync for one customer (Force=true, so an
 * existing avatar is overwritten). Fire-and-forget: the worker downloads the
 * cover of the customer's latest final album; the avatar appears after a
 * refetch, not synchronously.
 */
export function useSyncAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await api.POST("/customers/{id}/sync-avatar", {
        params: { path: { id } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Sync avatar thất bại"));
      return data;
    },
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: customerKeys.all });
      qc.invalidateQueries({ queryKey: customerKeys.detail(id) });
    },
  });
}

/**
 * useSyncAllAvatars — enqueue avatar sync for every customer without an avatar
 * in the caller's branch scope. `limit` undefined uses the server's bounded
 * default batch (1000); a number caps the batch. Returns `{ enqueued, hasMore }`.
 */
export function useSyncAllAvatars() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (limit?: number) => {
      const { data, error } = await api.POST("/customers/sync-avatar", {
        params: { query: limit != null ? { limit } : {} },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Sync avatar hàng loạt thất bại"));
      return data; // { enqueued, hasMore }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: customerKeys.all }),
  });
}

// ══════════ NOTES (cross-order CRM + customer-level) ══════════

export function useCustomerNotes(id: string | null, limit = 50) {
  return useQuery({
    queryKey: customerKeys.notes(id ?? ""),
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await api.GET("/customers/{id}/notes", {
        params: { path: { id: id! }, query: { limit } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được ghi chú"));
      return data;
    },
    staleTime: 15_000,
  });
}

export function useAddCustomerNote(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CustomerNoteInput) => {
      const { data, error } = await api.POST("/customers/{id}/notes", {
        params: { path: { id: customerId } },
        body: input,
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không lưu được ghi chú"));
      return data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: customerKeys.notes(customerId) }),
  });
}

export function useUpdateNote(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ noteId, input }: { noteId: string; input: NoteUpdateInput }) => {
      const { data, error } = await api.PUT("/notes/{noteId}", {
        params: { path: { noteId } },
        body: input,
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không cập nhật được ghi chú"));
      return data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: customerKeys.notes(customerId) }),
  });
}

export function useDeleteNote(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (noteId: string) => {
      const { error } = await api.DELETE("/notes/{noteId}", {
        params: { path: { noteId } },
      });
      if (error) throw new Error(serverErrorMessage(error, "Không xoá được ghi chú"));
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: customerKeys.notes(customerId) }),
  });
}

// ══════════ HỒ SƠ 360° — khiếu nại / đánh giá / chat của một khách ══════════
// Mỗi cửa giữ nguyên cổng quyền và phạm vi chi nhánh của mảng chủ; máy chủ khớp
// khách theo 9 số cuối điện thoại (cùng luật đếm "Tổng đơn").

export type CustomerComplaint = components["schemas"]["CskhComplaintWithOrder"];
export type CustomerReview = components["schemas"]["ReviewListItem"];
export type CustomerChat = components["schemas"]["ChatAdminCustomer"];

export function useCustomerComplaints(id: string, enabled = true) {
  return useQuery({
    queryKey: customerKeys.complaints(id),
    enabled: enabled && !!id,
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/complaints", {
        params: { query: { customer_id: id } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được khiếu nại của khách"));
      return data;
    },
  });
}

export function useCustomerReviews(id: string, enabled = true) {
  return useQuery({
    queryKey: customerKeys.reviews(id),
    enabled: enabled && !!id,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/reviews", {
        params: { query: { customer_id: id } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được đánh giá của khách"));
      return data.items ?? [];
    },
  });
}

/** null = khách chưa từng chat, hoặc mọi luồng nằm ngoài phạm vi người xem. */
export function useCustomerChat(id: string, enabled = true) {
  return useQuery({
    queryKey: customerKeys.chat(id),
    enabled: enabled && !!id,
    staleTime: 30_000,
    queryFn: async (): Promise<CustomerChat | null> => {
      const { data, error } = await api.GET("/customers/{id}/chat", {
        params: { path: { id } },
      });
      if (error || !data) throw new Error(serverErrorMessage(error, "Không tải được cuộc chat của khách"));
      return data.item ?? null;
    },
  });
}

// ══════════ ĐỔI SĐT / EMAIL (QĐ 0434) ══════════
// Một cửa ba bước: "check" (CENTER dryRun, không ghi gì) → "apply" → "resume"
// (nút "Hoàn tất" khi CENTER chưa trả lời). Máy chủ: customer/contact_change_*.go.

export type ContactChangeInput = components["schemas"]["CustomerContactChangeInput"];
export type ContactCheckResult = components["schemas"]["CustomerContactCheckResult"];
export type ContactApplyResult = components["schemas"]["CustomerContactApplyResult"];
export type PendingContactChange = components["schemas"]["CustomerPendingContactChange"];

/** Lỗi đổi liên hệ mang kèm mã máy để màn phân nhánh ("Hoàn tất" khi 504). */
export class ContactChangeError extends Error {
  constructor(
    message: string,
    readonly code: string,
  ) {
    super(message);
    this.name = "ContactChangeError";
  }
}

/** Mã máy của hai ca "đổi dở": CENTER chưa trả lời / GNPF chưa ghi xong. */
export const CONTACT_CHANGE_UNFINISHED_CODES = ["contact_change_unknown", "contact_change_incomplete"];

async function postContactChange(id: string, input: ContactChangeInput) {
  const { data, error } = await api.POST("/customers/{id}/contact-change", {
    params: { path: { id } },
    body: input,
  });
  if (error || !data) {
    throw new ContactChangeError(serverErrorMessage(error, "Không đổi được liên hệ của khách"), serverErrorCode(error));
  }
  return data;
}

export async function getPendingContactChange(id: string): Promise<PendingContactChange | null> {
  const { data, error } = await api.GET("/customers/{id}", { params: { path: { id } } });
  if (error || !data) throw new Error(serverErrorMessage(error, "Chưa đọc được yêu cầu đổi dở"));
  return data.pendingContactChange ?? null;
}

export function useCheckContactChange() {
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: Omit<ContactChangeInput, "step"> }) =>
      (await postContactChange(id, { ...input, step: "check" })) as ContactCheckResult,
  });
}

export function useApplyContactChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: ContactChangeInput }) =>
      (await postContactChange(id, input)) as ContactApplyResult,
    // Cả khi lỗi: yêu cầu đổi có thể vừa thành "đang dở" — trang khách phải
    // đọc lại để hiện khung "Hoàn tất".
    onSettled: (_data, _err, vars) => {
      qc.invalidateQueries({ queryKey: customerKeys.all });
      qc.invalidateQueries({ queryKey: customerKeys.detail(vars.id) });
      qc.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

// ══════════ CHUYỂN ĐƠN GIỮA HAI HỒ SƠ (QĐ 0471) ══════════
// Mở từ hộp Đổi số / Đổi email: số / email mới thuộc hồ sơ có dữ liệu (mã
// `contact_holder_has_data`) ⇒ chọn đơn chuyển sang đó; hoặc tách một phần đơn
// sang số mới. Cùng khuôn ba bước. Máy chủ: customer/order_transfer_*.go.

export type OrderTransferInput = components["schemas"]["CustomerOrderTransferInput"];
export type OrderTransferCheckResult = components["schemas"]["CustomerOrderTransferCheckResult"];
export type OrderTransferApplyResult = components["schemas"]["CustomerOrderTransferApplyResult"];
export type PendingOrderTransfer = components["schemas"]["CustomerPendingOrderTransfer"];

/** Mã máy: số / email mới thuộc hồ sơ có dữ liệu ⇒ mở nhánh chọn đơn. */
export const CONTACT_HOLDER_HAS_DATA = "contact_holder_has_data";
/** Mã máy của hai ca "chuyển dở": CENTER chưa trả lời / GNPF chưa ghi xong. */
export const ORDER_TRANSFER_UNFINISHED_CODES = ["order_transfer_unknown", "order_transfer_incomplete"];

async function postOrderTransfer(id: string, input: OrderTransferInput) {
  const { data, error } = await api.POST("/customers/{id}/order-transfer", {
    params: { path: { id } },
    body: input,
  });
  if (error || !data) {
    throw new ContactChangeError(serverErrorMessage(error, "Không chuyển được đơn của khách"), serverErrorCode(error));
  }
  return data;
}

export async function getPendingOrderTransfer(id: string): Promise<PendingOrderTransfer | null> {
  const { data, error } = await api.GET("/customers/{id}", { params: { path: { id } } });
  if (error || !data) throw new Error(serverErrorMessage(error, "Chưa đọc được lượt chuyển đơn dở"));
  return data.pendingOrderTransfer ?? null;
}

export function useCheckOrderTransfer() {
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: Omit<OrderTransferInput, "step"> }) =>
      (await postOrderTransfer(id, { ...input, step: "check" })) as OrderTransferCheckResult,
  });
}

export function useApplyOrderTransfer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: OrderTransferInput }) =>
      (await postOrderTransfer(id, input)) as OrderTransferApplyResult,
    // Cả khi lỗi: lượt chuyển có thể vừa thành "đang dở"; hồ sơ nguồn có thể
    // vừa bị gỡ — trang khách và danh sách đơn phải đọc lại.
    onSettled: (_data, _err, vars) => {
      qc.invalidateQueries({ queryKey: customerKeys.all });
      qc.invalidateQueries({ queryKey: customerKeys.detail(vars.id) });
      qc.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}
